import { apiClient } from './apiClient';
import { Client, User } from '../types';

/**
 * Service for client management
 */
export class ClientsService {
  // Mock clients for development
  private mockClients: Client[] = [
    { id: 'client_1', name: 'McDonalds', headquartersAddress: 'Chicago, IL' },
    { id: 'client_2', name: 'Walmart', headquartersAddress: 'Bentonville, AR' },
    { id: 'client_3', name: 'Target', headquartersAddress: 'Minneapolis, MN' }
  ];

  /**
   * Get all clients for the company
   */
  async getClients(): Promise<Client[]> {
    try {
      // Try to make a real API call first
      const response = await apiClient.get<Client[]>('/clients');
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.log('Using mock clients data due to API error:', error);
      return [...this.mockClients];
    }
    
    // Fallback to mock data
    return [...this.mockClients];
  }

  /**
   * Get client by ID
   */
  async getClientById(id: string): Promise<Client> {
    try {
      // Try to make a real API call first
      const response = await apiClient.get<Client>(`/clients/${id}`);
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.log('Using mock client data due to API error:', error);
      
      // Find the client in mock data
      const client = this.mockClients.find(l => l.id === id);
      
      if (client) {
        return { ...client };
      }
      
      throw new Error('Client not found');
    }
    
    // Fallback to mock data
    const client = this.mockClients.find(l => l.id === id);
    
    if (client) {
      return { ...client };
    }
    
    throw new Error('Client not found');
  }

  /**
   * Create a new client
   */
  async createClient(name: string, headquartersAddress?: string): Promise<Client> {
    try {
      // Try to make a real API call first
      const response = await apiClient.post<Client>('/clients', { name, headquartersAddress });
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (error) {
      console.log('Using mock client creation due to API error:', error);
      
      // Create a new client with a generated ID
      const newClient: Client = {
        id: `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name,
        headquartersAddress
      };
      
      // Add to mock clients
      this.mockClients.push(newClient);
      
      return { ...newClient };
    }
    
    // Fallback to mock creation
    const newClient: Client = {
      id: `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      headquartersAddress
    };
    
    // Add to mock clients
    this.mockClients.push(newClient);
    
    return { ...newClient };
  }

  /**
   * Update a client
   */
  async updateClient(id: string, name: string, headquartersAddress?: string): Promise<boolean> {
    try {
      // Try to make a real API call first
      const response = await apiClient.put<void>(`/clients/${id}`, { name, headquartersAddress });
      
      if (response.success) {
        return true;
      }
    } catch (error) {
      console.log('Using mock client update due to API error:', error);
      
      // Find the client to update
      const clientIndex = this.mockClients.findIndex(l => l.id === id);
      
      if (clientIndex === -1) {
        throw new Error('Client not found');
      }
      
      // Update client data
      this.mockClients[clientIndex] = {
        ...this.mockClients[clientIndex],
        name,
        headquartersAddress
      };
      
      return true;
    }
    
    // Fallback to mock update
    const clientIndex = this.mockClients.findIndex(l => l.id === id);
    
    if (clientIndex === -1) {
      throw new Error('Client not found');
    }
    
    // Update client data
    this.mockClients[clientIndex] = {
      ...this.mockClients[clientIndex],
      name,
      headquartersAddress
    };
    
    return true;
  }

  /**
   * Delete a client
   */
  async deleteClient(id: string): Promise<boolean> {
    try {
      // Try to make a real API call first
      const response = await apiClient.delete<void>(`/clients/${id}`);
      
      if (response.success) {
        return true;
      }
    } catch (error) {
      console.log('Using mock client deletion due to API error:', error);
      
      // Find the client to delete
      const clientIndex = this.mockClients.findIndex(l => l.id === id);
      
      if (clientIndex === -1) {
        throw new Error('Client not found');
      }
      
      // Remove from mock clients
      this.mockClients.splice(clientIndex, 1);
      
      return true;
    }
    
    // Fallback to mock deletion
    const clientIndex = this.mockClients.findIndex(l => l.id === id);
    
    if (clientIndex === -1) {
      throw new Error('Client not found');
    }
    
    // Remove from mock clients
    this.mockClients.splice(clientIndex, 1);
    
    return true;
  }
}

// Create a default instance
export const clientsService = new ClientsService();

export default clientsService;
